<template>
    <!-- Main container -->
    <div class="final-wrapper">
        <!-- Camera view - shown when less than 3 photos captured -->
        <div class="camera-container" v-if="capturedImages.length < 3">
            <!-- Face position instruction -->
            <div class="message">{{ faceTexts[capturedImages.length] }}</div>
            <!-- Camera area -->
            <div class="camera">
                <!-- Face position preview - reversed for the last photo -->
                <div class="preview" :class="[capturedImages.length === 2 ? 'reverse' : '']">
                    <img :src="faceSides[capturedImages.length]" />
                </div>
                <!-- Photo capture effect -->
                <div class="capture-effect" v-show="showCaptureEffect" @animationend="showCaptureEffect = false"></div>
                <!-- Camera video stream -->
                <video ref="videoElement" class="camera-feed" autoplay playsinline></video>
                <!-- Hidden canvas for photo capture -->
                <canvas ref="canvas" style="display: none;"></canvas>
            </div>
            <!-- Camera control buttons -->
            <div class="camera-controls">
                <!-- Camera switch button -->
                <div class="btn-icon" @click="switchCamera">
                    <img :src="ASSETS.IMAGES.FORM.SWITCH_CAM" />
                </div>
                <!-- Photo capture button -->
                <div class="btn-icon cam" @click="captureImage">
                    <div class="innercircle"></div>
                </div>
                <!-- Info button -->
                <div class="btn-icon" @click="showInfo">
                    <img :src="ASSETS.IMAGES.FORM.INFO" />
                </div>
            </div>
        </div>
        <!-- Upload view - shown after 3 photos are captured -->
        <div class="upload-container" v-else>
            <!-- Title -->
            <div class="title">Nasıl görünüyor?</div>
            <!-- Captured photos preview -->
            <div class="image-container">
                <!-- Left column - front face photo -->
                <div class="col-left">
                    <div class="image-taken" :style="`background-image: url(${capturedImages[0].url})`">
                        <div class="preview">
                            <img :src="faceSides[0]" />
                        </div>
                    </div>
                </div>
                <!-- Right column - side face photos -->
                <div class="col-right">
                    <div class="image-taken" :style="`background-image: url(${capturedImages[1].url})`">
                        <div class="preview">
                            <img :src="faceSides[1]" />
                        </div>
                    </div>
                    <div class="image-taken" :style="`background-image: url(${capturedImages[2].url})`">
                        <div class="preview reverse">
                            <img :src="faceSides[2]" />
                        </div>
                    </div>
                </div>
            </div>
            <!-- Info message -->
            <div class="info">
                <div class="info-icon">
                    <img :src="ASSETS.IMAGES.FORM.INFO" />
                </div>
                <div class="info-text">
                    Lütfen fotoğraflarınızın kılavuzumuza uygun olduğundan emin olun.
                </div>
            </div>
            <!-- Action buttons -->
            <div class="buttons">
                <!-- Retake button -->
                <div class="retake" @click="retake">
                    <img :src="ASSETS.IMAGES.REFRESH_WHITE" />
                    <span>Yeniden Çek</span>
                </div>
                <!-- Upload button -->
                <div class="upload" @click="createNewAvatar">Yükle</div>
            </div>
            <!-- Upload status overlay -->
            <div class="uploading-overlay" v-if="uploading">
                <div class="uploading-wrapper">
                    <!-- Loading animation -->
                    <div class="loader" :style="`background-image: url(${ASSETS.IMAGES.FORM.BORDER})`">
                        <div class="rotator rotating-element" :style="`background-image: url(${ASSETS.IMAGES.FORM.LOADING})`"></div>
                        <div class="face" :style="`background-image: url(${ASSETS.IMAGES.FORM.FACE})`"></div>
                        <div class="percentage">{{uploadProgress}} %</div>
                    </div>
                    <!-- Upload status texts -->
                    <div class="title">Yükleniyor</div>
                    <div class="desc">Fotoğraflarınız yükleniyor</div>
                    <!-- Logo -->
                    <div class="logo"><img :src="ASSETS.IMAGES.LOGO" /></div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { API, ASSETS } from '@/constants/urls';
import { getFaceOrientationImage } from '@/constants/pathUtils';

/**
 * CameraComponent
 * 
 * A component for capturing user photos to create an avatar.
 * It handles camera access, photo capture, and uploading photos to create a 3D avatar.
 * 
 * @component
 */
export default {
    props: {
        /** Function to show information overlay */
        info: Function,
        
        /** User ID for avatar creation */
        uid: String,
        
        /** Callback function when avatar creation is completed */
        completed: Function,
        
        /** User's gender for avatar creation */
        gender: String
    },
    data() {
        return {
            /** API access token */
            token: API.TOKEN,
            
            /** Camera stream object */
            stream: null,
            
            /** Current camera device ID */
            currentDeviceId: null,
            
            /** List of all video input devices */
            allVideoInputs: [],
            
            /** Array of captured images */
            capturedImages: [],
            
            /** Face side index tracker */
            faceSideIndex: 0,
            
            /** Upload progress percentage */
            uploadProgress: 0,
            
            /** Avatar ID after creation */
            avatarId: null,
            
            /** Upload in progress flag */
            uploading: false,
            
            /** Capture effect visibility flag */
            showCaptureEffect: false,
            
            /** Face direction instruction texts */
            faceTexts: ['Lütfen doğrudan kameraya bakın', 'Kafanızı sağa çevirin.', 'Kafanızı sola çevirin.'],
            
            /** Face orientation guide images */
            faceSides: [
                getFaceOrientationImage(0),
                getFaceOrientationImage(1),
                getFaceOrientationImage(2)
            ],
            
            /** Asset constants for template usage */
            ASSETS
        };
    },
    mounted() {
        // Initialize camera feed when component loads
        this.getCameraFeed();
    },
    methods: {
        /**
         * Reset captured images and restart camera feed
         */
        retake() {
            this.capturedImages = [];
            this.getCameraFeed();
        },
        
        /**
         * Initialize and get camera video feed
         * @async
         */
        async getCameraFeed() {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                this.allVideoInputs = devices.filter(device => device.kind === 'videoinput');

                if (this.allVideoInputs.length > 0) {
                    this.currentDeviceId = this.allVideoInputs[0].deviceId;

                    this.stream = await navigator.mediaDevices.getUserMedia({
                        video: { deviceId: this.currentDeviceId }
                    });
                    this.$refs.videoElement.srcObject = this.stream;
                    this.$refs.videoElement.style.transform = 'scaleX(-1)';
                } else {
                    alert('Kamera bulunamadı.');
                }
            } catch (err) {
                alert('Kamera erişimi reddedildi.');
                console.error(err);
            }
        },

        /**
         * Create a new avatar from captured photos
         * @async
         */
         async createNewAvatar() {
            // Stop camera before uploading
            this.stopCamera();
            this.uploading = true;

            try {
                // Request a new avatar
                const response = await fetch(`${API.AVATURN}${API.ENDPOINTS.AVATAR_NEW}`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${API.TOKEN}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        user_id: this.uid
                    })
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                console.log('New avatar created:', data);

                this.avatarId = data.avatar_id;
                this.uploadAvatarFiles(data.upload_url);

            } catch (error) {
                console.error('Error in createNewAvatar:', error);
                this.uploading = false;
            }
        },
        
        /**
         * Upload avatar photo files to the service
         * @param {string} uploadUrl - URL to upload files to
         */
         async uploadAvatarFiles(uploadUrl) {
            if (!uploadUrl) {
                console.error('Upload URL is missing!');
                this.uploading = false;
                return;
            }

            // Ensure gender is in correct format ('male' or 'female')
            // Avatarun accepts 'male' od 'female' only
            const genderValue = this.gender.toLowerCase() === 'erkek' ? 'male' : 'female';
            
            const formData = new FormData();
            formData.append('body-type', genderValue);
            formData.append('telephoto', 'false');

            if (this.capturedImages.length >= 3) {
                formData.append('image-frontal', this.capturedImages[0].blob, 'image-frontal.jpg');
                formData.append('image-side-1', this.capturedImages[1].blob, 'image-side-1.jpg');
                formData.append('image-side-2', this.capturedImages[2].blob, 'image-side-2.jpg');
            } else {
                console.error('Not enough images captured for upload!');
                this.uploading = false;
                return;
            }

            console.log('Uploading images to:', uploadUrl);
            console.log('FormData Entries:', Array.from(formData.entries()));

            try {
                const response = await fetch(uploadUrl, {
                    method: 'POST',
                    body: formData 
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(`Upload failed! Status: ${response.status}, Error: ${JSON.stringify(errorData)}`);
                }

                const result = await response.json();
                console.log('Upload successful:', result);

                this.uploading = false;
                this.completed(this.avatarId); // Call completion function

            } catch (error) {
                console.error('Upload error:', error);
                this.uploading = false;
            }
        },
        
        /**
         * Capture an image from the camera
         */
        captureImage() {
            this.showCaptureEffect = true;
            const videoElement = this.$refs.videoElement;
            const canvas = this.$refs.canvas;
            canvas.width = videoElement.videoWidth;
            canvas.height = videoElement.videoHeight;

            const context = canvas.getContext('2d');
            context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(blob => {
                this.capturedImages.push({
                    url: URL.createObjectURL(blob),
                    blob: blob
                });

                console.log('Number of captured images:', this.capturedImages.length);
            }, 'image/png');
        },

        /**
         * Switch between available cameras
         */
        switchCamera() {
            const currentIndex = this.allVideoInputs.findIndex(device => device.deviceId === this.currentDeviceId);
            if (currentIndex !== -1 && this.allVideoInputs.length > 1) {
                const nextIndex = (currentIndex + 1) % this.allVideoInputs.length;
                this.currentDeviceId = this.allVideoInputs[nextIndex].deviceId;
                this.getCameraFeed();
            }
        },
        
        /**
         * Show information overlay
         */
        showInfo() {
            this.info();
        },
        
        /**
         * Stop camera stream
         */
        stopCamera() {
            if (this.stream) {
                this.stream.getTracks().forEach(track => track.stop());
                this.stream = null;
            }
        },
    },
    beforeUnmount() {
        // Stop camera when component is unmounted
        this.stopCamera();
    },
};
</script>

<style scoped>
/* Percentage display */
.percentage {
    position: absolute;
    bottom: -20px;
    z-index: 2;
    color: white;
    left: 50%;
    transform: translateX(-50%);
    font-size: 25px;
}

/* Rotating animation and face image */
.rotator,
.face {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
}

/* Rotation animation */
@keyframes rotateAnimation {
    from {
        transform: translate(-50%, -50%) rotate(0deg);
    }

    to {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

/* Rotating element */
.rotating-element {
    animation: rotateAnimation 2s linear infinite;
}

/* Face image */
.face {
    width: 50px;
    height: 50px;
}

/* Rotator indicator */
.rotator {
    width: 110px;
    height: 110px;
}

/* Loading indicator */
.loader {
    width: 250px;
    height: 250px;
    background-size: contain;
    background-position: center;
    position: relative;
}

/* Loading wrapper */
.uploading-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

/* Uploading overlay */
.uploading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 20%;
    background: #333;
    z-index: 4;
}

/* Upload container */
.upload-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

/* Retake button */
.retake {
    color: white;
    max-width: 50%;
    margin: 0 auto;
    text-align: center;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: center;
    align-items: center;
}

/* Retake button image */
.retake img {
    width: 12px;
    height: 12px;
}

/* Upload button */
.upload {
    color: #333;
    background: white;
    border-radius: 32px;
    width: 50%;
    margin: 0 auto;
    padding: 10px 20px;
    text-align: center;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: center;
    align-items: center;
}

/* Buttons container */
.buttons {
    flex: 1;
    justify-content: flex-end;
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    padding: 40px;
    gap: 20px;
}

/* Image container */
.image-container {
    display: flex;
    height: -moz-fit-content;
    height: fit-content;
    height: 370px;
    justify-content: center;
    margin: 0 auto;
    max-width: 100%;
    width: calc(100% - 80px);
}

/* Title */
.title {
    text-align: center;
    font-size: 30px;
    color: white;
    padding: 20px 0;
}

/* Description */
.desc {
    font-size: 14px;
    color: white;
}

/* Left column */
.col-left {
    display: flex;
    margin-right: 16px;
    width: 65%;
}

/* Right column */
.col-right {
    display: flex;
    flex-flow: column;
    gap: 16px;
    width: 35%;
}

/* Info area */
.info {
    display: flex;
    gap: 10px;
    max-width: 40%;
    font-size: 12px;
    justify-content: center;
    align-items: center;
    margin: 20px auto;
}

/* Info icon */
.info-icon {
    padding-right: 10px;
    border-right: 1px solid #ccc;
}

/* Info icon image */
.info-icon img {
    width: 100%;
}

/* Info text */
.info-text {
    color: white;
}

/* Captured image */
.image-taken {
    align-items: center;
    background-position: 50%;
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: 16px;
    display: flex;
    height: 100%;
    justify-content: center;
    -o-object-fit: cover;
    object-fit: cover;
    position: relative;
    width: 100%;
}

/* Preview */
.preview {
    position: absolute;
    top: 10px;
    right: 10px;
    border-radius: 15px;
    overflow: hidden;
    max-width: 50px;
    z-index: 2;
}

/* Reversed preview */
.preview.reverse {
    transform: rotateY(180deg);
}

/* Preview image */
.preview img {
    width: 100%;
}

/* Preview in captured image */
.image-taken .preview {
    max-width: 30px;
    border-radius: 4px;
}

/* Message */
.message {
    position: absolute;
    top: 10px;
    left: 20px;
    right: 20px;
    color: white;
    font-size: 14px;
    text-align: center;
}

/* Camera container */
.camera-container,
.upload-container,
.final-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
}

/* Camera */
.camera {
    flex: 1;
    margin: 15% 10% 10px 10%;
    border-radius: 25px;
    overflow: hidden;
    max-height: 500px;
    position: relative;
}

/* Camera feed */
.camera-feed {
    object-fit: cover;
    height: 100%;
    width: 100%;
    transform: scaleX(1);
}

/* Camera controls */
.camera-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    position: relative;
}

/* Capture effect */
.capture-effect {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: white;
    opacity: 0;
    animation: flash 0.5s forwards;
    z-index: 3;
}

/* Flash animation */
@keyframes flash {
    0% {
        opacity: 0;
    }

    50% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
}

/* Button icon */
.btn-icon img {
    max-width: 100%;
}

/* Button icon container */
.btn-icon {
    width: 32px;
    height: 32px;
    margin: 20px;
}

/* Logo image */
.logo img {
    max-width: 90px;
}

/* Logo container */
.logo {
    position: absolute;
    bottom: 10px;
}

/* Camera button */
.btn-icon.cam {
    width: 80px;
    height: 80px;
    border: 4px solid white;
    border-radius: 50%;
}

/* Inner circle */
.innercircle {
    margin: 5px;
    background: white;
    border-radius: 50%;
    width: calc(100% - 10px);
    height: calc(100% - 10px);
}

/* Mobile view */
@media only screen and (max-width: 450px) {
    .info {
        max-width: 70%;
    }

    .info-icon {
        width: 50px;
    }

    .buttons>div {
        width: 70%;
        max-width: 70%;
    }
}
</style>